import React, { useCallback } from "react";
import cx from "clsx";
import PropTypes from "prop-types";
import ReactSelect, { components as reactSelectComponents } from "react-select";
import { THEME } from "../../consts/theme";
import { svg } from "../../assets/svg";
import { Z_INDEXES } from "../../consts/styleVariables";
import classes from "./styles.module.scss";

const SELECT_SIZES = {
  medium: "medium",
  small: "small",
};

export function Select({
  value,
  onChange,
  options,
  autoFocus,
  className,
  placeholder,
  isSearchable,
  isMulti,
  isDisabled,
  isError,
  size,
  isFixed,
  closeMenuOnSelect,
  isLoading,
  inputValue,
  onInputChange,
  components,
  isClearable,
  menuListFooter,
  whiteChevron,
  noOptionsMessage,
  isOptionClearable,
  fullWidth,
}) {
  const getBorderColor = (state) => {
    let result = THEME.colors.gray1;
    if (state.isFocused && !isError) {
      result = THEME.colors.primary;
    }
    if (isError) {
      result = THEME.colors.red;
    }
    return result;
  };

  const getBackgroundColor = (state) => {
    let result = THEME.colors.white;
    if (state.isDisabled) {
      result = THEME.colors.gray4;
    }
    if (isError) {
      result = THEME.colors.redDim2;
    }
    return result;
  };

  const getMultiValueBackgroundColor = () => {
    let result = THEME.colors.primaryDim;
    if (isError) {
      result = THEME.colors.redLighten;
    }
    if (isDisabled) {
      result = THEME.colors.gray2;
    }
    return result;
  };

  const getOptionColor = ({ isDisabled, isSelected }) => {
    if (isSelected && !isDisabled) {
      return THEME.colors.white;
    }
    if (isSelected) {
      return THEME.colors.white;
    }
    if (!isDisabled) {
      return THEME.colors.black;
    }
    return THEME.colors.gray0;
  };

  const getOptionBackgroundColor = ({ isSelected, isDisabled, fallback }) => {
    if (isSelected && !isDisabled) {
      return THEME.colors.primary;
    }
    if (isSelected) {
      return THEME.colors.primaryDim1;
    }
    if (!isDisabled) {
      return fallback;
    }
    return THEME.colors.gray4;
  };

  const DropdownIndicator = useCallback((props) => {
    return (
      <reactSelectComponents.DropdownIndicator {...props}>
        <img
          src={!whiteChevron ? svg.chevronDown : svg.chevronDownWhite}
          alt="chevron-down"
          className={props.selectProps.menuIsOpen ? "rotate-180" : ""}
        />
      </reactSelectComponents.DropdownIndicator>
    );
  }, []);

  const ClearIndicator = useCallback((props) => {
    const {
      getStyles,
      innerProps: { ref, ...restInnerProps },
    } = props;
    return (
      <div
        {...restInnerProps}
        ref={ref}
        style={getStyles("clearIndicator", props)}
      >
        <img src={svg.close} alt="x" width="11px" />
      </div>
    );
  }, []);

  const MenuList = useCallback((props) => {
    return (
      <>
        <reactSelectComponents.MenuList {...props}>
          {props.children}
          {menuListFooter}
        </reactSelectComponents.MenuList>
      </>
    );
  }, []);

  const Option = (props) => {
    return (
      <reactSelectComponents.Option {...props}>
        <div style={{ display: "flex", gap: "8px" }}>
          {props.data.icon}
          {props.label}
        </div>
      </reactSelectComponents.Option>
    );
  };

  const MultiValueLabel = (props) => {
    return (
      <reactSelectComponents.MultiValueLabel {...props}>
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          {props.data.icon}
          {props.children}
        </div>
      </reactSelectComponents.MultiValueLabel>
    );
  };

  const NoOptionsMessage = (props) => {
    const { children, ...rest } = props;

    return (
      <reactSelectComponents.NoOptionsMessage {...rest}>
        {noOptionsMessage || children}
      </reactSelectComponents.NoOptionsMessage>
    );
  };

  const minHeightBySize = {
    [SELECT_SIZES.medium]: "44px",
    [SELECT_SIZES.small]: "36px",
  };

  const orderOptions = (values) => {
    if (Array.isArray(values) && isOptionClearable) {
      const fixed = values.filter((v) => !isOptionClearable(v));
      const clearable = values.filter((v) => isOptionClearable(v));

      return [...fixed, ...clearable];
    }

    return values;
  };

  const onChange_ = (newValue, actionMeta) => {
    if (isOptionClearable) {
      switch (actionMeta.action) {
        case "remove-value":
        case "pop-value":
        case "clear":
          if (!isOptionClearable(actionMeta.removedValue)) {
            return;
          }
          break;
      }
    }

    onChange(orderOptions(newValue));
  };

  return (
    <ReactSelect
      inputValue={inputValue}
      isClearable={isClearable}
      onInputChange={onInputChange}
      value={value}
      onChange={onChange_}
      options={options}
      autoFocus={autoFocus}
      className={cx(
        {
          [classes.fullWidth]: fullWidth,
        },
        className,
      )}
      placeholder={placeholder}
      isSearchable={isSearchable}
      isMulti={isMulti}
      isDisabled={isDisabled}
      isLoading={isLoading}
      closeMenuOnSelect={closeMenuOnSelect}
      menuPortalTarget={document.body}
      components={{
        Option,
        MultiValueLabel,
        MenuList,
        DropdownIndicator,
        ClearIndicator,
        NoOptionsMessage,
        ...components,
      }}
      menuPosition={isFixed ? "fixed" : "absolute"}
      styles={{
        menuPortal: (base) => ({
          ...base,
          zIndex: Z_INDEXES.max,
        }),
        control: (baseStyles, state) => ({
          ...baseStyles,
          borderColor: getBorderColor(state),
          backgroundColor: getBackgroundColor(state),
          minHeight: minHeightBySize[size] || "50px",
          padding: "0 5px",
          fontSize: "16px",
          fontWeight: 400,
          borderWidth: "1px",
          borderRadius: size === SELECT_SIZES.small ? "4px" : "8px",
          boxShadow: "none",
          ":hover": {
            ...(isError && { borderColor: THEME.colors.red }),
          },
        }),
        indicatorSeparator: (baseStyles) => ({
          ...baseStyles,
          display: "none",
        }),
        multiValue: (base) => ({
          ...base,
          backgroundColor: getMultiValueBackgroundColor(),
          borderRadius: "5px",
          "& > *": {
            whiteSpace: "break-spaces !important",
          },
        }),
        placeholder: (base) => ({
          ...base,
          ...(isError && { color: THEME.colors.red }),
        }),
        option: (base, { isSelected, isDisabled }) => ({
          ...base,
          color: getOptionColor({ isSelected, isDisabled }),
          padding: `${size === "small" ? 4 : 8}px 12px !important`,
          backgroundColor: getOptionBackgroundColor({
            isSelected,
            isDisabled,
            fallback: base.backgroundColor,
          }),
        }),
        multiValueRemove: (base, state) => {
          if (isOptionClearable) {
            return isOptionClearable(state.data)
              ? base
              : { ...base, display: "none" };
          }

          return base;
        },
      }}
      theme={(t) => ({
        ...t,
        colors: {
          ...t.colors,
          primary25: THEME.colors.primaryDim,
          primary: THEME.colors.primary,
          neutral80: THEME.colors.black,
        },
      })}
    />
  );
}

const selectOptionPropType = PropTypes.shape({
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
  ]).isRequired,
  isDisabled: PropTypes.bool,
});

Select.propTypes = {
  value: PropTypes.oneOfType([
    selectOptionPropType,
    PropTypes.arrayOf(selectOptionPropType),
  ]),
  onChange: PropTypes.func,
  options: PropTypes.arrayOf(selectOptionPropType),
  autoFocus: PropTypes.bool,
  className: PropTypes.string,
  placeholder: PropTypes.string,
  isSearchable: PropTypes.bool,
  isMulti: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isError: PropTypes.bool,
  isFixed: PropTypes.bool,
  size: PropTypes.oneOf(Object.values(SELECT_SIZES)),
  closeMenuOnSelect: PropTypes.bool,
  isLoading: PropTypes.bool,
  inputValue: PropTypes.string,
  onInputChange: PropTypes.func,
  components: PropTypes.object,
  isClearable: PropTypes.bool,
  menuListFooter: PropTypes.node,
  noOptionsMessage: PropTypes.string,
  isOptionClearable: PropTypes.func,
};

Select.defaultProps = {
  value: undefined,
  onChange: () => {},
  options: [],
  autoFocus: false,
  className: undefined,
  placeholder: undefined,
  isSearchable: false,
  isMulti: false,
  isDisabled: false,
  isError: false,
  size: undefined,
  isFixed: false,
  closeMenuOnSelect: true,
  isLoading: false,
  inputValue: undefined,
  onInputChange: undefined,
  components: {},
  isClearable: false,
  noOptionsMessage: undefined,
};
